import { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './WorkoutRoutine.css';

// ── API types ─────────────────────────────────────────────────────────────────

interface ApiExercise {
  order_index: number;
  sets: number | null;
  reps: number | null;
  duration_seconds: number | null;
  rest_seconds: number | null;
  weight_kg: number | null;
  slot_notes: string | null;
  exercise_id: string;
  name: string;
  category: string;
  duration_type: string | null;
  difficulty: string | null;
  primary_muscles: string[];
  secondary_muscles: string[];
  instructions: string | null;
  cues: string[];
  images: string[];
  videos: string[];
}

interface PlanContext {
  plan_name: string;
  duration_weeks: number;
  focus: string | null;
  week_number: number;
  day_number: number;
  completed_at: string | null;
  day_notes: string | null;
  days_completed: number;
  total_training_days: number;
}

interface WorkoutData {
  id: string;
  nanoid: string;
  name: string;
  description: string | null;
  modality: string;
  duration_minutes: number | null;
  difficulty: string | null;
  exercises: ApiExercise[];
  plan: PlanContext | null;
}

// ── Internal exercise type ────────────────────────────────────────────────────

interface Exercise {
  id: string;
  name: string;
  description: string;
  muscles: string[];
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  images: string[];
  videos: string[];   // looping demo videos (Veo); preferred over images when present
  tips: string[];
  sets?: number;
  reps?: number;
  duration?: number;   // seconds — timed exercises
  rest?: number;       // seconds
  isCardio: boolean;   // cardio/running exercises use elapsed timer
  runNotes?: string;   // pace/effort guidance shown prominently for cardio
}

function mapDifficulty(d: string | null): Exercise['difficulty'] {
  if (d === 'intermediate') return 'Intermediate';
  if (d === 'advanced') return 'Advanced';
  return 'Beginner';
}

function mapExercises(apiExercises: ApiExercise[]): Exercise[] {
  return apiExercises.map(e => {
    const isCardio = e.category === 'cardio' || e.category === 'running';
    const muscles = [
      ...(e.primary_muscles ?? []),
      ...(e.secondary_muscles ?? []),
    ].map(m => m.charAt(0).toUpperCase() + m.slice(1));

    return {
      id:          e.exercise_id,
      name:        e.name,
      description: e.instructions ?? '',
      muscles:     muscles.length > 0 ? muscles : [],
      difficulty:  mapDifficulty(e.difficulty),
      images:      e.images ?? [],
      videos:      e.videos ?? [],
      tips:        e.cues ?? [],
      sets:        e.sets ?? undefined,
      reps:        e.reps ?? undefined,
      duration:    e.duration_seconds ?? undefined,
      rest:        e.rest_seconds ?? undefined,
      isCardio,
      runNotes:    isCardio ? (e.slot_notes ?? undefined) : undefined,
    };
  });
}

const DIFFICULTY_COLOR: Record<string, string> = {
  Beginner:     '#4caf87',
  Intermediate: '#f5a623',
  Advanced:     '#e05252',
};

// ── Audio ─────────────────────────────────────────────────────────────────────

let _audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext | null {
  try {
    if (!_audioCtx || _audioCtx.state === 'closed') _audioCtx = new AudioContext();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    return _audioCtx;
  } catch { return null; }
}

function tone(ctx: AudioContext, freq: number, start: number, dur: number, vol = 0.22) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'sine';
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(vol, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
  osc.start(start);
  osc.stop(start + dur + 0.05);
}

function playAlertSound() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  tone(ctx, 523, ctx.currentTime, 0.12);
}

function playCompleteSound() {
  const ctx = getAudioCtx();
  if (!ctx) return;
  const t = ctx.currentTime;
  tone(ctx, 523, t,        0.14);
  tone(ctx, 659, t + 0.14, 0.14);
  tone(ctx, 784, t + 0.28, 0.22);
}

// ── Countdown timer ───────────────────────────────────────────────────────────

function CountdownTimer({
  duration,
  size = 160,
  autoStart = true,
  onComplete,
}: {
  duration: number;
  size?: number;
  autoStart?: boolean;
  onComplete?: () => void;
}) {
  const [timeLeft, setTimeLeft] = useState(duration);
  const [running, setRunning] = useState(autoStart);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => { setTimeLeft(duration); setRunning(autoStart); }, [duration, autoStart]);

  const prevRunningRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (prevRunningRef.current !== null) playAlertSound();
    prevRunningRef.current = running;
  }, [running]);

  useEffect(() => {
    if (!running || timeLeft <= 0) {
      if (timeLeft <= 0) { playCompleteSound(); onCompleteRef.current?.(); }
      return;
    }
    const t = setTimeout(() => setTimeLeft(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timeLeft, running]);

  const done = timeLeft === 0;
  const pristine = timeLeft === duration && !running;
  const r = (size - 12) / 2;
  const circumference = 2 * Math.PI * r;
  const progress = timeLeft / duration;
  const offset = circumference * (1 - progress);
  const ringColor = done ? '#4caf87' : !running && !pristine ? '#aaa'
    : progress > 0.5 ? '#4caf87' : progress > 0.25 ? '#f5a623' : '#e05252';

  return (
    <div className="wr-timer-wrapper">
      <div className="wr-timer" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#ebebeb" strokeWidth="8" />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={ringColor} strokeWidth="8"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            transform={`rotate(-90 ${size/2} ${size/2})`}
            style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.4s ease' }} />
        </svg>
        <div className="wr-timer-text" style={{ color: ringColor }}>
          {done ? '✓' : `${timeLeft}`}
        </div>
      </div>
      {!done && (
        <button
          className={`wr-timer-control ${running ? 'wr-timer-control--pause' : 'wr-timer-control--start'}`}
          onClick={() => setRunning(r => !r)}
        >
          {running ? 'Pause' : pristine ? 'Start' : 'Resume'}
        </button>
      )}
    </div>
  );
}

// ── Elapsed timer (cardio / running) ─────────────────────────────────────────

function ElapsedTimer({ targetSeconds }: { targetSeconds?: number }) {
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  const display = `${mins}:${String(secs).padStart(2, '0')}`;

  const targetDisplay = targetSeconds
    ? `${Math.floor(targetSeconds / 60)}:${String(targetSeconds % 60).padStart(2, '0')}`
    : null;

  return (
    <div className="wr-elapsed-wrapper">
      <div className="wr-elapsed-time">{display}</div>
      {targetDisplay && (
        <div className="wr-elapsed-target">Target: {targetDisplay}</div>
      )}
      <button
        className={`wr-timer-control ${running ? 'wr-timer-control--pause' : 'wr-timer-control--start'}`}
        onClick={() => { playAlertSound(); setRunning(r => !r); }}
      >
        {running ? 'Pause' : elapsed === 0 ? 'Start' : 'Resume'}
      </button>
    </div>
  );
}

// ── Image placeholder (shown when no images available) ────────────────────────

function ExercisePlaceholder({ isCardio }: { isCardio: boolean }) {
  return (
    <div className="wr-image-frame wr-image-placeholder">
      {isCardio ? (
        <svg className="wr-placeholder-svg" viewBox="0 0 80 64" fill="none">
          <circle cx="52" cy="10" r="7" fill="#d4d4d4"/>
          <path d="M52 17 C48 22 44 26 42 32" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
          <path d="M42 32 L34 46" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
          <path d="M42 32 L50 48" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
          <path d="M46 22 L58 26" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
          <path d="M46 22 L38 18" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      ) : (
        <svg className="wr-placeholder-svg" viewBox="0 0 80 64" fill="none">
          <circle cx="40" cy="10" r="7" fill="#d4d4d4"/>
          <line x1="40" y1="17" x2="40" y2="40" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
          <line x1="24" y1="26" x2="56" y2="26" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
          <line x1="40" y1="40" x2="28" y2="58" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
          <line x1="40" y1="40" x2="52" y2="58" stroke="#d4d4d4" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      )}
    </div>
  );
}

// ── Image animation ───────────────────────────────────────────────────────────

function buildCycle(length: number): number[] {
  if (length === 1) return [0];
  const forward = Array.from({ length }, (_, i) => i);
  const back = Array.from({ length: length - 2 }, (_, i) => length - 2 - i);
  return [...forward, ...back];
}

function AnimatedExerciseImage({ images }: { images: string[] }) {
  const cycle = buildCycle(images.length);
  const [activeIdx, setActiveIdx] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setActiveIdx(0);
    if (images.length === 1) return;
    intervalRef.current = setInterval(() => setActiveIdx(p => (p + 1) % cycle.length), 1600);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [images]);

  if (images.length === 0) return null;

  return (
    <div className="wr-image-frame">
      {images.map((src, i) => (
        <img key={src} src={src} alt={`Exercise step ${i + 1}`}
          className={`wr-exercise-img ${cycle[activeIdx] === i ? 'wr-img-visible' : 'wr-img-hidden'}`} />
      ))}
      {images.length > 1 && (
        <div className="wr-step-dots">
          {cycle.slice(0, images.length).map((_, i) => (
            <span key={i} className={`wr-dot ${cycle[activeIdx] === i ? 'wr-dot-active' : ''}`} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Video demo (preferred over images when a Veo clip exists) ─────────────────

function ExerciseVideo({ src, name }: { src: string; name: string }) {
  return (
    <div className="wr-image-frame">
      <video
        key={src}
        className="wr-exercise-video"
        src={src}
        muted
        loop
        autoPlay
        playsInline
        preload="auto"
        aria-label={`${name} demonstration`}
      />
    </div>
  );
}

// ── Screens ───────────────────────────────────────────────────────────────────

function OverviewScreen({
  exercises,
  workout,
  onStart,
}: {
  exercises: Exercise[];
  workout: WorkoutData;
  onStart: () => void;
}) {
  const estimated = Math.round(
    exercises.reduce((acc, ex) => {
      const work = ex.duration ?? (ex.reps ?? 0) * 3;
      return acc + work * (ex.sets ?? 1) + (ex.rest ?? 0);
    }, 0) / 60
  );

  return (
    <div className="wr-screen wr-overview">
      <div className="wr-overview-header">
        {workout.plan && (
          <div className="wr-badge">
            {workout.plan.plan_name} — Week {workout.plan.week_number}, Day {workout.plan.day_number}
          </div>
        )}
        {!workout.plan && <div className="wr-badge">Today's Workout</div>}
        <h1 className="wr-title">{workout.name}</h1>
        <p className="wr-subtitle">
          {exercises.length} exercises
          {(workout.duration_minutes || estimated > 0) && (
            <> &middot; ~{workout.duration_minutes ?? estimated} min</>
          )}
          {workout.difficulty && <> &middot; {workout.difficulty.charAt(0).toUpperCase() + workout.difficulty.slice(1)}</>}
        </p>
        {workout.plan && (
          <p className="wr-plan-progress">
            {workout.plan.days_completed} of {workout.plan.total_training_days} training days complete
          </p>
        )}
      </div>

      <div className="wr-exercise-list">
        {exercises.map((ex, idx) => (
          <div key={ex.id + idx} className="wr-exercise-card">
            <div className="wr-card-num">{idx + 1}</div>
            <div className="wr-card-info">
              <div className="wr-card-name">{ex.name}</div>
              <div className="wr-card-meta">
                {ex.isCardio && ex.duration
                  ? `${Math.floor(ex.duration / 60)} min`
                  : ex.sets && ex.reps
                  ? `${ex.sets} sets × ${ex.reps} reps`
                  : ex.duration
                  ? `${ex.duration}s hold`
                  : null}
                {ex.rest ? ` · ${ex.rest}s rest` : ''}
              </div>
              {ex.muscles.length > 0 && (
                <div className="wr-card-muscles">{ex.muscles.slice(0, 3).join(', ')}</div>
              )}
            </div>
            <span className="wr-difficulty-badge" style={{
              background: DIFFICULTY_COLOR[ex.difficulty] + '22',
              color:      DIFFICULTY_COLOR[ex.difficulty],
            }}>
              {ex.difficulty}
            </span>
          </div>
        ))}
      </div>

      <button className="wr-btn-primary" onClick={onStart}>Get Started</button>
    </div>
  );
}

function ExerciseScreen({
  exercise,
  index,
  total,
  onComplete,
}: {
  exercise: Exercise;
  index: number;
  total: number;
  onComplete: () => void;
}) {
  const [timerDone, setTimerDone] = useState(false);
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLast = index + 1 >= total;
  const isTimed = !exercise.isCardio && !!exercise.duration;

  function handleTimerComplete() {
    setTimerDone(true);
    autoAdvanceRef.current = setTimeout(onComplete, 1500);
  }

  function handleManualComplete() {
    if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);
    onComplete();
  }

  useEffect(() => () => { if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current); }, []);

  return (
    <div className="wr-screen wr-exercise-screen">
      <div className="wr-progress-bar">
        <div className="wr-progress-fill" style={{ width: `${(index / total) * 100}%` }} />
      </div>
      <div className="wr-progress-label">Exercise {index + 1} of {total}</div>

      <h2 className="wr-exercise-name">{exercise.name}</h2>
      {exercise.description && (
        <p className="wr-exercise-desc">{exercise.description}</p>
      )}

      {exercise.videos.length > 0
        ? <ExerciseVideo src={exercise.videos[0]} name={exercise.name} />
        : exercise.images.length > 0
        ? <AnimatedExerciseImage images={exercise.images} />
        : <ExercisePlaceholder isCardio={exercise.isCardio} />}

      {exercise.isCardio ? (
        <div className="wr-timer-section">
          {exercise.runNotes && (
            <div className="wr-run-notes">{exercise.runNotes}</div>
          )}
          <ElapsedTimer targetSeconds={exercise.duration} />
        </div>
      ) : isTimed ? (
        <div className="wr-timer-section">
          <CountdownTimer duration={exercise.duration!} size={160} autoStart={false}
            onComplete={handleTimerComplete} />
          {timerDone && <div className="wr-timer-done-label">Nice work!</div>}
        </div>
      ) : (
        <div className="wr-stat-row">
          <div className="wr-stat">
            <span className="wr-stat-val">{exercise.sets ?? '—'}</span>
            <span className="wr-stat-label">Sets</span>
          </div>
          <div className="wr-stat-divider" />
          <div className="wr-stat">
            <span className="wr-stat-val">{exercise.reps ?? '—'}</span>
            <span className="wr-stat-label">Reps</span>
          </div>
          {exercise.duration && (
            <>
              <div className="wr-stat-divider" />
              <div className="wr-stat">
                <span className="wr-stat-val">{exercise.duration}s</span>
                <span className="wr-stat-label">Hold</span>
              </div>
            </>
          )}
        </div>
      )}

      {exercise.muscles.length > 0 && (
        <div className="wr-muscles">
          {exercise.muscles.map(m => (
            <span key={m} className="wr-muscle-chip">{m}</span>
          ))}
        </div>
      )}

      {exercise.tips.length > 0 && (
        <div className="wr-tips">
          <div className="wr-tips-header">Form Tips</div>
          <ul className="wr-tips-list">
            {exercise.tips.map((tip, i) => <li key={i}>{tip}</li>)}
          </ul>
        </div>
      )}

      <button className="wr-btn-primary wr-btn-complete" onClick={handleManualComplete}>
        {exercise.isCardio
          ? isLast ? 'Done — Finish Workout' : 'Done — Next Exercise'
          : timerDone
          ? isLast ? 'Finish Workout' : 'Next Exercise'
          : isTimed
          ? 'Skip Timer'
          : isLast ? 'Finish Workout' : 'Complete & Next'}
      </button>
      {isLast && (
        <p className="wr-log-hint">This workout will be logged to your activity history once complete.</p>
      )}
    </div>
  );
}

function RestScreen({
  seconds,
  nextExercise,
  onDone,
}: {
  seconds: number;
  nextExercise: Exercise;
  onDone: () => void;
}) {
  return (
    <div className="wr-screen wr-rest-screen">
      <div className="wr-rest-label">Rest</div>
      <CountdownTimer duration={seconds} size={180} onComplete={onDone} />
      <div className="wr-next-up">
        <div className="wr-next-label">Next up</div>
        <div className="wr-next-name">{nextExercise.name}</div>
        <div className="wr-next-meta">
          {nextExercise.isCardio && nextExercise.duration
            ? `${Math.floor(nextExercise.duration / 60)} min`
            : nextExercise.sets && nextExercise.reps
            ? `${nextExercise.sets} sets × ${nextExercise.reps} reps`
            : nextExercise.duration
            ? `${nextExercise.duration}s hold`
            : ''}
        </div>
      </div>
      <button className="wr-btn-secondary" onClick={onDone}>Skip Rest</button>
    </div>
  );
}

function DoneScreen({
  total,
  plan,
  onReset,
}: {
  total: number;
  plan: PlanContext | null;
  onReset: () => void;
}) {
  return (
    <div className="wr-screen wr-done">
      <div className="wr-done-icon">&#10003;</div>
      <h1 className="wr-title">Great work!</h1>
      <p className="wr-subtitle">
        {plan
          ? `Day ${plan.day_number} of week ${plan.week_number} complete. ${plan.days_completed} of ${plan.total_training_days} training days done — keep it up.`
          : `You finished all ${total} exercises. Consistency is how progress happens.`}
      </p>
      <div className="wr-done-stats">
        <div className="wr-stat">
          <span className="wr-stat-val">{total}</span>
          <span className="wr-stat-label">Exercises</span>
        </div>
        <div className="wr-stat-divider" />
        <div className="wr-stat">
          <span className="wr-stat-val">&#10003;</span>
          <span className="wr-stat-label">Complete</span>
        </div>
      </div>
      <button className="wr-btn-secondary" onClick={onReset}>Start Over</button>
    </div>
  );
}

// ── Root component ────────────────────────────────────────────────────────────

type Phase = 'overview' | 'exercise' | 'rest' | 'done';

export default function WorkoutRoutine() {
  const { nanoid } = useParams<{ nanoid: string }>();

  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [workout, setWorkout]   = useState<WorkoutData | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);

  const [phase, setPhase]           = useState<Phase>('overview');
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    if (!nanoid) { setError('No workout ID in URL.'); setLoading(false); return; }
    fetch(`/api/workouts/${nanoid}`)
      .then(r => r.json())
      .then((data: { success: boolean; workout?: WorkoutData; error?: string }) => {
        if (!data.success || !data.workout) throw new Error(data.error ?? 'Workout not found');
        setWorkout(data.workout);
        setExercises(mapExercises(data.workout.exercises));
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [nanoid]);

  function handleStart() { setCurrentIdx(0); setPhase('exercise'); }

  function handleExerciseComplete() {
    const hasNext = currentIdx + 1 < exercises.length;
    if (!hasNext) { markComplete(); return; }
    const restSeconds = exercises[currentIdx].rest;
    if (restSeconds && restSeconds > 0) setPhase('rest');
    else setCurrentIdx(i => i + 1);
  }

  function handleRestDone() { setCurrentIdx(i => i + 1); setPhase('exercise'); }

  function handleReset() { setCurrentIdx(0); setPhase('overview'); }

  function markComplete() {
    setPhase('done');
    if (!nanoid) return;
    fetch(`/api/workouts/${nanoid}/complete`, { method: 'POST' }).catch(() => {});
  }

  return (
    <div className="wr-page">
      <PublicHeader />

      <main className="wr-root">
        <div className="wr-container">
          {loading && (
            <div className="wr-state-screen">
              <div className="wr-spinner" />
              <p className="wr-state-msg">Loading your workout...</p>
            </div>
          )}

          {!loading && error && (
            <div className="wr-state-screen">
              <p className="wr-state-msg wr-state-error">
                {error === 'Workout not found'
                  ? 'This workout link is no longer active.'
                  : 'Could not load workout. Check your connection and try again.'}
              </p>
            </div>
          )}

          {!loading && !error && workout && (
            <>
              {phase === 'overview' && (
                <OverviewScreen exercises={exercises} workout={workout} onStart={handleStart} />
              )}
              {phase === 'exercise' && (
                <ExerciseScreen
                  key={currentIdx}
                  exercise={exercises[currentIdx]}
                  index={currentIdx}
                  total={exercises.length}
                  onComplete={handleExerciseComplete}
                />
              )}
              {phase === 'rest' && currentIdx + 1 < exercises.length && (
                <RestScreen
                  key={`rest-${currentIdx}`}
                  seconds={exercises[currentIdx].rest!}
                  nextExercise={exercises[currentIdx + 1]}
                  onDone={handleRestDone}
                />
              )}
              {phase === 'done' && (
                <DoneScreen
                  total={exercises.length}
                  plan={workout.plan}
                  onReset={handleReset}
                />
              )}
            </>
          )}
        </div>
      </main>

      <footer className="wr-footer">
        <div className="wr-footer-inner">
          <div className="wr-footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="wr-footer-logo" />
            <span>mosaime</span>
          </div>
          <div className="wr-footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="wr-footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
